import { useMemo } from 'react'
import { Currency, CurrencyAmount, FactoryVersion, Pair } from '@nominex/nomiswap-sdk'
import { Interface } from '@ethersproject/abi'
import ILpTokenContract from 'config/abi/ILpTokenContract.json'
import { useWagmi } from 'libs/wagmi'
import { useMultipleContractSingleData } from 'state/multicall/hooks'
import { wrappedCurrency } from 'utils/wrappedCurrency'

const IPair = new Interface(ILpTokenContract)

export enum PairState {
  LOADING,
  NOT_EXISTS,
  EXISTS,
  INVALID,
}

export function usePairs(
  currencies: [Currency | undefined, Currency | undefined][],
  factoryVersion?: FactoryVersion,
): [PairState, Pair | null][] {
  const { chainId } = useWagmi()

  const tokens = useMemo(
    () =>
      currencies.map(([currencyA, currencyB]) => [
        wrappedCurrency(currencyA, chainId),
        wrappedCurrency(currencyB, chainId),
      ]),
    [chainId, currencies],
  )

  const pairAddresses = useMemo(
    () =>
      tokens.map(([tokenA, tokenB]) =>
        tokenA && tokenB && !tokenA.equals(tokenB) ? Pair.getAddress(tokenA, tokenB, factoryVersion) : undefined,
      ),
    [tokens, factoryVersion],
  )

  const reservesCallResults = useMultipleContractSingleData(pairAddresses, IPair, 'getReserves')
  const swapFeeCallResults = useMultipleContractSingleData(pairAddresses, IPair, 'swapFee')

  return useMemo(
    () =>
      tokens.map((tokensPair, i) => {
        const { result: rawReserves, loading: reservesLoading } = reservesCallResults[i]
        const { result: rawSwapFee, loading: swapFeeLoading } = swapFeeCallResults[i]

        if (reservesLoading || swapFeeLoading) {
          return [PairState.LOADING, null]
        }

        const [tokenA, tokenB] = tokensPair

        if (!tokenA || !tokenB || tokenA.equals(tokenB)) {
          return [PairState.INVALID, null]
        }

        if (!rawReserves || rawReserves?.reserve0?.eq(0) || rawReserves?.reserve1?.eq(0)) {
          return [PairState.NOT_EXISTS, null]
        }

        const [reserve0, reserve1] = rawReserves

        const [swapFee] = rawSwapFee
        const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA]
        const pair = new Pair(
          CurrencyAmount.fromRawAmount(token0, reserve0.toString()),
          CurrencyAmount.fromRawAmount(token1, reserve1.toString()),
          swapFee,
          factoryVersion,
        )

        return [PairState.EXISTS, pair]
      }),
    [reservesCallResults, swapFeeCallResults, tokens, factoryVersion],
  )
}

export function usePair(
  tokenA?: Currency,
  tokenB?: Currency,
  factoryVersion?: FactoryVersion,
): [PairState, Pair | null] {
  const pairCurrencies = useMemo<[Currency, Currency][]>(() => [[tokenA, tokenB]], [tokenA, tokenB])
  return usePairs(pairCurrencies, factoryVersion)[0]
}
